import { ApolloLink } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { fromPromise } from '@apollo/client/link/utils'

import logger from 'shared/services/logger'
import { isServerError } from 'shared/utils/type-guards'
import { logoutUser, attemptTokenRefresh, redirectTo } from './error.helper'
import { UserError } from 'shared/utils/errors'

// Re-export redirectTo for backwards compatibility
export { redirectTo }

/**
 * Error handler function for Apollo error link
 * Handles authentication failures with automatic retry
 *
 * Strategy:
 * - Skip retry on server (SSR) - token is already refreshed before Apollo runs
 * - On 401: Attempt token refresh and retry request once
 * - On refresh success: Retry with new token silently
 * - On refresh failure (explicitly unauthorized or forbidden error on refresh): Log out user
 * - On refresh failure (other network error): Show error, keep user logged in
 * - On 403: No retry, no logout (permission issue, not auth)
 * - Otherwise, log the error but do not log the user out
 *
 * @internal Exported for testing purposes
 */
export function handleAuthError({
  networkError,
  operation,
  forward,
}: Parameters<Parameters<typeof onError>[0]>[0]): ReturnType<
  Parameters<typeof onError>[0]
> {
  // Don't intercept CurrentUserContext errors
  if (operation.operationName.match(/CurrentUserContext/)) {
    return
  }

  // Log non-401 network errors (these are never retried, always actionable)
  if (
    networkError &&
    (!isServerError(networkError) || networkError.statusCode !== 401)
  ) {
    logger.error(networkError, {
      key: 'graphql-network-error',
      feature: logger.Feature.Auth,
      extra: {
        operationName: operation.operationName,
        statusCode: isServerError(networkError)
          ? networkError.statusCode
          : undefined,
      },
    })
    return
  }

  // Only handle 401 Unauthorized errors for retry
  if (!isServerError(networkError) || networkError.statusCode !== 401) {
    return
  }

  //
  // Skip retry logic on server (SSR)
  // Token is already refreshed in getPageRouterSession() before Apollo runs
  // Let the error bubble to getServerSideProps for proper redirect handling
  //
  // TODO: I think we could also possibly request a refreshed session with our getSession, but for now I'm gonna let the server requests remain as they are
  //
  if (typeof window === 'undefined') {
    logger.debug(
      `GraphQL 401 on server for ${operation.operationName} - skipping retry (handle at getServerSideProps level)`,
    )
    return
  }

  // Check if we've already retried this operation
  const context = operation.getContext()
  if (context.hasRetried401) {
    logger.error(
      `GraphQL request failed after retry: ${operation.operationName}`,
      {
        key: 'graphql-auth-retry-failed',
        feature: logger.Feature.Auth,
        extra: {
          operationName: operation.operationName,
          statusCode: 401,
          timestamp: new Date().toISOString(),
        },
      },
    )

    // Already retried once, logout user
    logoutUser(operation.operationName, 'Token refresh retry failed')
    return
  }

  logger.debug(
    `GraphQL 401 error, attempting token refresh for: ${operation.operationName}`,
  )

  // Attempt to refresh the session and retry the request
  return fromPromise(
    attemptTokenRefresh()
      .then((accessToken) => {
        logger.debug(
          `Token refreshed successfully, retrying: ${operation.operationName}`,
        )

        // Update operation context with new token and retry flag
        operation.setContext({
          hasRetried401: true,
          headers: {
            ...context.headers,
            authorization: `Bearer ${accessToken}`,
          },
        })

        return accessToken
      })
      .catch((error) => {
        // Distinguish between auth failures and other failures
        if (error instanceof UserError) {
          // Auth-related failure - session is truly invalid, logout
          logoutUser(
            operation.operationName,
            `Session refresh failed: ${error.constructor.name}`,
          )
          throw error
        }

        // Network or other error - don't logout, let error bubble up
        // (error already logged in attemptTokenRefresh)
        throw error
      }),
  )
    .filter((value) => Boolean(value)) // Wait for the promise to resolve
    .flatMap(() => forward(operation)) // Retry the operation
}

/**
 * Apollo error link with retry logic
 */
export const errorLinkOnError = onError(handleAuthError)

const errorLink = (): ApolloLink => errorLinkOnError

export default errorLink
